import { useState, useEffect } from 'react';
import { api, ApiError } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type ChangeType = 'reserve_change' | 'status_change' | 'data_update';

interface Handler {
  user_id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  user_job: string;
  job_label: string;
}

interface CreateChangeRequest {
  claim_id: string;
  claim_number: string;
  request_type: 'reserve_change' | 'status_change' | 'data_update' | 'party_update';
  handler_id: string;
  handler_name: string;
  changes: Array<{
    field: string;
    field_label: string;
    old_value: string;
    new_value: string;
    display_format?: string;
    reserve_type_id?: string;
  }>;
  reason: string;
  priority: 'normal' | 'urgent';
}

interface CurrentValues {
  total_outstanding: number | null;
  total_paid: number | null;
  total_incurred: number | null;
  loss_reserve: number | null;
  lae_reserve: number | null;
  open_close_status: string | null;
  insured_name: string | null;
  coverage_type: string | null;
  defense_counsel: string | null;
  controlling_state: string | null;
  deductible_amt: string | null;
}

interface Props {
  claimId: string;
  claimNumber: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

const RESERVE_FIELDS = [
  { value: 'total_outstanding', label: 'Total Outstanding', reserve_type_id: '' },
  { value: 'loss_reserve', label: 'Loss Reserve (LOSS)', reserve_type_id: '000001' },
  { value: 'lae_reserve', label: 'LAE Reserve (LAE)', reserve_type_id: '000002' },
];

const DATA_UPDATE_FIELDS = [
  { value: 'insured_name', label: 'Insured Name' },
  { value: 'coverage_type', label: 'Coverage Type' },
  { value: 'defense_counsel', label: 'Defense Counsel' },
  { value: 'controlling_state', label: 'Controlling State' },
  { value: 'deductible_amt', label: 'Deductible Amount' },
];

const STATUS_OPTIONS = [
  { value: 'O', label: 'Open' },
  { value: 'C', label: 'Closed' },
  { value: 'R', label: 'Reopened' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function ChangeRequestForm({
  claimId,
  claimNumber,
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  // Handler selection
  const [handlers, setHandlers] = useState<Handler[]>([]);
  const [selectedHandlerId, setSelectedHandlerId] = useState('');
  const [loadingHandlers, setLoadingHandlers] = useState(false);

  const [changeType, setChangeType] = useState<ChangeType>('reserve_change');
  const [currentValues, setCurrentValues] = useState<CurrentValues>({
    total_outstanding: null,
    total_paid: null,
    total_incurred: null,
    loss_reserve: null,
    lae_reserve: null,
    open_close_status: null,
    insured_name: null,
    coverage_type: null,
    defense_counsel: null,
    controlling_state: null,
    deductible_amt: null,
  });

  // Reserve Change fields
  const [reserveField, setReserveField] = useState('total_outstanding');
  const [newReserveAmount, setNewReserveAmount] = useState('');

  // Status Change fields
  const [newStatus, setNewStatus] = useState('O');

  // Data Update fields
  const [dataField, setDataField] = useState('insured_name');
  const [newDataValue, setNewDataValue] = useState('');

  // Shared fields
  const [reason, setReason] = useState('');
  const [priority, setPriority] = useState<'normal' | 'urgent'>('normal');

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [validationWarnings, setValidationWarnings] = useState<any[]>([]);
  const [showWarningConfirm, setShowWarningConfirm] = useState(false);
  const [pendingBody, setPendingBody] = useState<any>(null);
  const [success, setSuccess] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Load current values on open
  useEffect(() => {
    if (!isOpen) return;

    // Reset form state
    setChangeType('reserve_change');
    setSelectedHandlerId('');
    setNewReserveAmount('');
    setReserveField('total_outstanding');
    setNewStatus('O');
    setDataField('insured_name');
    setNewDataValue('');
    setReason('');
    setPriority('normal');
    setSubmitError(null);
    setValidationErrors([]);
    setValidationWarnings([]);
    setShowWarningConfirm(false);
    setPendingBody(null);
    setSuccess(false);

    // Load handlers
    if (handlers.length === 0) {
      setLoadingHandlers(true);
      api.getHandlers()
        .then((res: any) => {
          if (res?.handlers) setHandlers(res.handlers);
        })
        .catch(() => {})
        .finally(() => setLoadingHandlers(false));
    }

    // Attempt to load preview — fall back gracefully on failure
    setLoadingPreview(true);
    const fields = [
      'total_outstanding',
      'total_paid',
      'total_incurred',
      'loss_reserve',
      'lae_reserve',
      'open_close_status',
      'insured_name',
      'coverage_type',
      'defense_counsel',
      'controlling_state',
      'deductible_amt',
    ];
    (api as any)
      .getChangePreview(claimId, fields)
      .then((res: any) => {
        if (res && res.current_values) {
          setCurrentValues((prev) => ({ ...prev, ...res.current_values }));
        }
      })
      .catch(() => {
        // Preview endpoint not deployed yet — silently ignore
      })
      .finally(() => setLoadingPreview(false));
  }, [isOpen, claimId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  // ── Derived ─────────────────────────────────────────────────────────────────

  const parsedNewReserve = parseFloat(newReserveAmount);
  const currentReserveValue = (currentValues[reserveField as keyof CurrentValues] as number) ?? 0;
  const delta = !isNaN(parsedNewReserve) ? parsedNewReserve - currentReserveValue : null;

  const currentDataValue = (() => {
    const val = currentValues[dataField as keyof CurrentValues];
    if (val === null || val === undefined) return '';
    return String(val);
  })();

  // ── Submit ───────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    if (!selectedHandlerId) {
      setSubmitError('Please select a handler.');
      return;
    }
    if (!reason.trim()) {
      setSubmitError('Reason is required.');
      return;
    }

    const changes: CreateChangeRequest['changes'] = [];

    if (changeType === 'reserve_change') {
      if (!newReserveAmount || isNaN(parsedNewReserve)) {
        setSubmitError('Please enter a valid reserve amount.');
        return;
      }
      const reserveFieldDef = RESERVE_FIELDS.find((f) => f.value === reserveField);
      const fieldLabel = reserveFieldDef?.label ?? reserveField;
      const entry: CreateChangeRequest['changes'][0] = {
        field: reserveField,
        field_label: fieldLabel,
        old_value: String(currentReserveValue),
        new_value: String(parsedNewReserve),
        display_format: 'currency',
      };
      if (reserveFieldDef?.reserve_type_id) {
        entry.reserve_type_id = reserveFieldDef.reserve_type_id;
      }
      changes.push(entry);
    } else if (changeType === 'status_change') {
      const oldStatus = currentValues.open_close_status ?? '';
      const newStatusLabel = STATUS_OPTIONS.find((s) => s.value === newStatus)?.label ?? newStatus;
      changes.push({
        field: 'open_close_status',
        field_label: 'Status',
        old_value: oldStatus,
        new_value: newStatus,
      });
      void newStatusLabel;
    } else if (changeType === 'data_update') {
      if (!newDataValue.trim()) {
        setSubmitError('Please enter a new value.');
        return;
      }
      const fieldLabel = DATA_UPDATE_FIELDS.find((f) => f.value === dataField)?.label ?? dataField;
      changes.push({
        field: dataField,
        field_label: fieldLabel,
        old_value: currentDataValue,
        new_value: newDataValue.trim(),
      });
    }

    const selectedHandler = handlers.find((h) => h.user_id === selectedHandlerId);
    const body: CreateChangeRequest = {
      claim_id: claimId,
      claim_number: claimNumber,
      request_type: changeType,
      handler_id: selectedHandlerId,
      handler_name: selectedHandler
        ? `${selectedHandler.last_name}, ${selectedHandler.first_name}`
        : '',
      changes,
      reason: reason.trim(),
      priority,
    };

    await submitChange(body);
  }

  async function submitChange(body: CreateChangeRequest & { skip_validation?: boolean }) {
    setSubmitting(true);
    setSubmitError(null);
    setValidationErrors([]);
    setValidationWarnings([]);
    setShowWarningConfirm(false);

    try {
      const result = await (api as any).createChange(body);

      // Check if the server returned validation warnings requiring confirmation
      if (result?.requires_confirmation && result?.validation_warnings?.length) {
        setValidationWarnings(result.validation_warnings);
        setPendingBody(body);
        setShowWarningConfirm(true);
        setSubmitting(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      if (err instanceof ApiError && err.data) {
        const data = err.data;
        if (data.validation_errors?.length) {
          setValidationErrors(data.validation_errors);
          if (data.validation_warnings?.length) {
            setValidationWarnings(data.validation_warnings);
          }
        } else if (data.errors?.length) {
          // Basic field validation errors
          setSubmitError(data.errors.join('; '));
        } else {
          setSubmitError(err.message || 'Failed to submit change request.');
        }
      } else {
        setSubmitError(err.message || 'Failed to submit change request.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmWarnings() {
    if (!pendingBody) return;
    await submitChange({ ...pendingBody, skip_validation: true });
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-xl mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Request a Change</h2>
            <p className="text-xs text-gray-500 mt-0.5">Claim {claimNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Success overlay */}
        {success && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 px-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-900">Change request submitted</p>
            <p className="text-xs text-gray-500">Awaiting supervisor review.</p>
          </div>
        )}

        {!success && (
          <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

            {/* Change type selector */}
            <div>
              <p className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">Change Type</p>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: 'reserve_change', label: 'Reserve Change' },
                  { value: 'status_change', label: 'Status Change' },
                  { value: 'data_update', label: 'Data Update' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setChangeType(opt.value as ChangeType)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                      changeType === opt.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400 hover:text-gray-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Handler selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned Handler <span className="text-red-500">*</span>
              </label>
              {loadingHandlers ? (
                <p className="text-xs text-gray-400">Loading handlers...</p>
              ) : (
                <select
                  value={selectedHandlerId}
                  onChange={(e) => setSelectedHandlerId(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a handler...</option>
                  {handlers.map((h) => (
                    <option key={h.user_id} value={h.user_id}>
                      {h.last_name}, {h.first_name} ({h.job_label})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="border-t border-gray-100" />

            {/* ── Reserve Change ──────────────────────────────────────── */}
            {changeType === 'reserve_change' && (
              <div className="space-y-4">
                {/* Current financials read-only */}
                <div>
                  <p className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">Current Financials</p>
                  {loadingPreview ? (
                    <p className="text-xs text-gray-400">Loading...</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'Outstanding', value: currentValues.total_outstanding },
                        { label: 'Loss Reserve', value: currentValues.loss_reserve },
                        { label: 'LAE Reserve', value: currentValues.lae_reserve },
                        { label: 'Paid', value: currentValues.total_paid },
                        { label: 'Incurred', value: currentValues.total_incurred },
                      ].map((item) => (
                        <div key={item.label} className="bg-gray-50 rounded-lg border border-gray-200 px-3 py-2">
                          <p className="text-xs text-gray-500">{item.label}</p>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">
                            {formatCurrency(item.value)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Reserve field selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reserve Field</label>
                  <select
                    value={reserveField}
                    onChange={(e) => setReserveField(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {RESERVE_FIELDS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>

                {/* New amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Reserve Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newReserveAmount}
                    onChange={(e) => setNewReserveAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Delta display */}
                {delta !== null && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Delta:</span>
                    <span
                      className={`font-semibold ${
                        delta > 0 ? 'text-red-600' : delta < 0 ? 'text-green-700' : 'text-gray-600'
                      }`}
                    >
                      {delta > 0 ? '+' : ''}{formatCurrency(delta)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Status Change ───────────────────────────────────────── */}
            {changeType === 'status_change' && (
              <div className="space-y-4">
                {/* Current status read-only */}
                <div>
                  <p className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-1">Current Status</p>
                  {loadingPreview ? (
                    <p className="text-xs text-gray-400">Loading...</p>
                  ) : (
                    <p className="text-sm text-gray-900">
                      {STATUS_OPTIONS.find((s) => s.value === currentValues.open_close_status)?.label ??
                        currentValues.open_close_status ??
                        '—'}
                    </p>
                  )}
                </div>

                {/* New status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label} ({s.value})</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* ── Data Update ─────────────────────────────────────────── */}
            {changeType === 'data_update' && (
              <div className="space-y-4">
                {/* Field selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field to Update</label>
                  <select
                    value={dataField}
                    onChange={(e) => {
                      setDataField(e.target.value);
                      setNewDataValue('');
                    }}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {DATA_UPDATE_FIELDS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>

                {/* Current value read-only */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Value</label>
                  {loadingPreview ? (
                    <p className="text-xs text-gray-400">Loading...</p>
                  ) : (
                    <div className="bg-gray-50 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 min-h-[36px]">
                      {currentDataValue || '—'}
                    </div>
                  )}
                </div>

                {/* New value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Value</label>
                  <input
                    type="text"
                    value={newDataValue}
                    onChange={(e) => setNewDataValue(e.target.value)}
                    placeholder="Enter new value"
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            <div className="border-t border-gray-100" />

            {/* Reason */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Reason <span className="text-red-500">*</span></label>
                <span className="text-xs text-gray-400">{reason.length}/500</span>
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                rows={3}
                placeholder="Explain why this change is needed..."
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Priority toggle */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Priority</p>
              <div className="flex gap-2">
                {(['normal', 'urgent'] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPriority(p)}
                    className={`px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors capitalize ${
                      priority === p
                        ? p === 'urgent'
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Validation errors */}
            {validationErrors.length > 0 && (
              <div className="space-y-2">
                {validationErrors.map((ve, i) => (
                  <div key={i} className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                    <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        {ve.type === 'stale_data' ? `Stale Data: ${ve.field_label || ve.field}` :
                         ve.type === 'claim_closed' ? 'Claim Closed' :
                         ve.type === 'claim_not_found' ? 'Claim Not Found' : 'Validation Error'}
                      </p>
                      <p className="text-xs text-red-700 mt-0.5">{ve.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Warning confirmation dialog */}
            {showWarningConfirm && validationWarnings.length > 0 && (
              <div className="space-y-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <p className="text-sm font-medium text-amber-800">Attention Required</p>
                {validationWarnings.map((vw, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <div>
                      <p className="text-sm text-amber-800 font-medium">
                        {vw.type === 'large_increase' ? `Large Increase: ${vw.field_label || vw.field}` :
                         vw.type === 'zero_reserve' ? `Zero Reserve: ${vw.field_label || vw.field}` :
                         vw.type === 'duplicate' ? `Duplicate Request: ${vw.field_label || vw.field}` : 'Warning'}
                      </p>
                      <p className="text-xs text-amber-700 mt-0.5">{vw.message}</p>
                    </div>
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleConfirmWarnings}
                    disabled={submitting}
                    className="px-3 py-1.5 text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Submitting...' : 'Submit Anyway'}
                  </button>
                  <button
                    onClick={() => { setShowWarningConfirm(false); setValidationWarnings([]); setPendingBody(null); }}
                    disabled={submitting}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Generic error */}
            {submitError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <p className="text-sm text-red-700">{submitError}</p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
